import axios from 'axios';
import type { ToastMessageOptions } from 'primevue/toast';

/** Shared toast durations (ms) so call sites don't each invent their own. */
export const TOAST_LIFE = {
  success: 2500,
  warn: 4000,
  error: 5000,
  apiError: 6000,
} as const;

type Handler = (opts: ToastMessageOptions) => void;

let handler: Handler | null = null;

export const setToastHandler = (fn: Handler | null): void => {
  handler = fn;
};

export const notify = (opts: ToastMessageOptions): void => {
  if (handler) {
    handler(opts);
  } else {
    console.warn('[notify] no toast handler registered yet:', opts);
  }
};

export const notifySuccess = (summary: string, detail?: string): void =>
  notify({ severity: 'success', summary, detail, life: TOAST_LIFE.success });

export const notifyWarn = (summary: string, detail?: string): void =>
  notify({ severity: 'warn', summary, detail, life: TOAST_LIFE.warn });

export const notifyError = (summary: string, detail?: string): void =>
  notify({ severity: 'error', summary, detail, life: TOAST_LIFE.error });

/**
 * Toast a failed store/API call from a component. Errors that went through the
 * `api` axios instance have already been toasted (with an error code) by the
 * response interceptor in boot/axios.ts, so only non-axios errors are shown
 * here — otherwise the user gets two toasts for the same failure.
 */
export const notifyRequestError = (summary: string, err: unknown): void => {
  if (axios.isAxiosError(err)) return;
  const detail = err instanceof Error && err.message ? err.message : 'Der opstod en uventet fejl.';
  notifyError(summary, detail);
};

/** Errors raised by the BarcodeScanner component: a string or an Error. */
export const notifyScanError = (error: unknown): void => {
  const detail =
    typeof error === 'string'
      ? error
      : error instanceof Error && error.message
        ? error.message
        : 'Stregkoden kunne ikke læses.';
  notifyWarn('Scanner-fejl', detail);
};
